import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DefaultValuesService } from '../../services/defaultvalues.service';
import { SpinnerOverlayService } from '../../services/spinner-overlay.service';
import { Utils } from '../../utils/utils';
import { ComponentTool } from '../../interfaces/componenttool';

@Component({
    selector: "wikinav",
    templateUrl: './wikinav.component.html',
    styleUrls: ['./wikinav.component.less'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class WikiNavComponent {

    isExpanded = false;
    @Input() hideInlineDoc: boolean = true;
    @ViewChild('rightSidenav', { static: true }) sidenav;
    markdown: string | null = '';
    remoteUrl = '';
    subscriptions: Subscription[] = [];

    @Output() disableWikiBtn = new EventEmitter<boolean>();
    @Output() showLoadingBar = new EventEmitter<boolean>();

    constructor(
        private readonly router: Router,
        private readonly http: HttpClient,
        private readonly route: ActivatedRoute,
        private readonly defaults: DefaultValuesService,
        @Inject('BASE_URL') private readonly baseUrl: string,
        private readonly spinnerOverlayService: SpinnerOverlayService
    ) {

    }
    ngOnInit() {
        this.defaults.routeChangedObservable.subscribe(() => {
            this.subscriptions.forEach(x => x.unsubscribe())
            setTimeout(() => {
                this.wikiApiCallsManager();
            }, 1000);
        });
    }
    ngOnChanges() {
        this.displayWikiManager()

    }
    displayWikiManager() {
        this.hideInlineDoc ? this.sidenav.close() : this.sidenav.open();

    }


    private static readonly DB_NAME = 'wiki-cache';
    private static readonly STORE = 'entries';
    private dbPromise: Promise<IDBDatabase> | null = null;

    private get isMersy(): boolean {
        return this.defaults.componentTool === ComponentTool.Mersy;
    }

    private get cacheKey(): string {
        return `${this.defaults.routeSeg}/${this.defaults.wikiTabName}`;
    }

    private openDb(): Promise<IDBDatabase> {
        this.dbPromise ??= new Promise((resolve, reject) => {
                const req = indexedDB.open(WikiNavComponent.DB_NAME, 1);
                req.onupgradeneeded = e => {
                    (e.target as IDBOpenDBRequest).result
                        .createObjectStore(WikiNavComponent.STORE, { keyPath: 'key' });
                };
                req.onsuccess = e => resolve((e.target as IDBOpenDBRequest).result);
                req.onerror = () => reject(req.error ?? new Error('Failed to open IndexedDB'));
            });
        return this.dbPromise;
    }

    private async loadFromCache(key: string): Promise<{ content: string; remoteUrl: string } | null> {
        try {
            const db = await this.openDb();
            return new Promise(resolve => {
                const req = db.transaction(WikiNavComponent.STORE).objectStore(WikiNavComponent.STORE).get(key);
                req.onsuccess = () => {
                    const e = req.result;
                    resolve(e?.date === new Date().toDateString() ? e : null);
                };
                req.onerror = () => resolve(null);
            });
        } catch { return null; }
    }

    private async saveToCache(key: string, content: string, remoteUrl: string): Promise<void> {
        try {
            const db = await this.openDb();
            const today = new Date().toDateString();
            await new Promise<void>(resolve => {
                const store = db.transaction(WikiNavComponent.STORE, 'readwrite').objectStore(WikiNavComponent.STORE);
                const req = store.openCursor();
                req.onsuccess = e => {
                    const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result;
                    if (cursor) {
                        if (cursor.value.date !== today) cursor.delete();
                        cursor.continue();
                    }
                    else resolve();
                };
                req.onerror = () => resolve();
            });
            const store = db.transaction(WikiNavComponent.STORE, 'readwrite').objectStore(WikiNavComponent.STORE);
            store.put({ key, date: today, content, remoteUrl });
        } catch (e) { console.error('[wiki cache] save failed:', e); }
    }

    async wikiApiCallsManager() {
        const key = this.cacheKey;
        if (this.isMersy) {
            const cached = await this.loadFromCache(key);
            if (cached !== null) {
                if (cached.content) {
                    this.markdown = cached.content;
                    this.remoteUrl = cached.remoteUrl;
                    this.disableWikiBtn.emit(false);
                    this.showLoadingBar.emit(false);
                    this.displayWikiManager();
                } else {
                    this.markdown = '';
                    this.isExpanded = false;
                    this.disableWikiBtn.emit(true);
                    this.showLoadingBar.emit(false);
                    this.sidenav.close();
                }
                return;
            }
        }
        this.getIsWikiAvailable(key);
    }

    toggleExpand() {
        this.isExpanded = !this.isExpanded;
    }

    getIsWikiAvailable(key: string) {
        this.showLoadingBar.emit(false);
        Utils.httpIsWikiAvailable(this.http
            , this.baseUrl
            , this.defaults.routeSeg
            , this.defaults.wikiTabName
            , this
            , function (tthis: WikiNavComponent, resp: any) {
                if (resp) {
                    tthis.getInlineWiki(key);
                } else {
                    if (tthis.isMersy) tthis.saveToCache(key, '', '');
                    tthis.markdown = '';
                    tthis.isExpanded = false;
                    tthis.disableWikiBtn.emit(true);
                    tthis.sidenav.close();
                }
            }
        )
    }

    getInlineWiki(key: string) {
        this.showLoadingBar.emit(true);
        Utils.httpGetInlineWiki(this.http
            , this.baseUrl, this.defaults.routeSeg, this.defaults.wikiTabName
            , this
            , function (tthis: WikiNavComponent, data: any) {
                tthis.showLoadingBar.emit(false);
                tthis.markdown = data.content;
                tthis.remoteUrl = data.remoteUrl;
                if (tthis.isMersy) tthis.saveToCache(key, data.content, data.remoteUrl);
                tthis.disableWikiBtn.emit(false);
                tthis.displayWikiManager();
            }
        )
    }

    openLinkInNewTab() {
        const linkUrl = this.remoteUrl;
        window.open(linkUrl, '_blank');
    }

}
